import { isStaleSessionError } from "@/engine/support/staleSfuSession";

/**
 * A media failure as a bounded category, for everything that leaves the browser.
 *
 * Provider bodies and SDP can carry credentials, addresses and identifiers, so
 * the message itself never travels; this is what does. The categories keep
 * apart the failures an investigation has to tell apart (#999): the provider
 * timing out at the server route, the provider refusing, and the browser's own
 * connection to the SFU failing all used to read `media_operation_failed`.
 */

/** The requests the `sfu-session` route forwards, as the broker names them. */
const SFU_ACTIONS = [
  "new-session",
  "push-tracks",
  "pull-tracks",
  "close-track",
  "renegotiate",
] as const;

type SfuAction = (typeof SFU_ACTIONS)[number];

export interface MediaFailureCause {
  reason: string;
  action?: SfuAction;
}

const CATEGORIES = [
  [/setRemoteDescription|setLocalDescription|sdp/i, "sdp_rejected"],
  [/429|rate.?limit/i, "rate_limited"],
  [/play|NotAllowedError/i, "playback_blocked"],
  [/stalled/i, "media_stalled"],
  [/SFU connection failed/, "connection_failed"],
  [/TimeoutError|timed out/i, "provider_timeout"],
  [/non-2xx status code/, "provider_rejected"],
] as const;

function sfuAction(message: string): SfuAction | undefined {
  const named = /^sfu-session ([a-z-]+):/.exec(message)?.[1];
  return SFU_ACTIONS.find((action) => action === named);
}

export function mediaFailureCause(message: string): MediaFailureCause {
  // The broker prefixes every error with "sfu-session", so a loose /session/i
  // would bucket any broker failure as a stale session; the transport's own
  // definition of "the session is gone" is the precise one.
  const reason = isStaleSessionError(message)
    ? "session_failed"
    : (CATEGORIES.find(([pattern]) => pattern.test(message))?.[1] ??
      "media_operation_failed");
  const action = sfuAction(message);
  return action ? { reason, action } : { reason };
}
